import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { DataStudioRedesignService } from 'app/shared/services/data-services/data-studio-redesign/data-studio-redesign.service';
import { Turbine } from 'app/shared/services/data-services/graphql';
import { Observable, Subject } from 'rxjs';
import { filter, skip, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { DataStudioHelperService } from './services/data-studio-helper.service';
import moment from 'moment';
import { DataStudioPageNavigationItem } from 'app/shared/services/data-services/navigation/navigation.interface';
import { CurrencyService } from 'app/shared/services/currency-service/currency.service';
import { TimezoneService } from 'app/shared/services/timezone/timezone.service';
import { DSSignalItemData, PreviewSerieType, QueryParams } from 'app/shared/models/data-studio.model';
import { DEVICE_TYPES } from './interfaces/data-studio.interface';
import { setSeries } from './helper/data-studio-signal.helper';

@Component({
  selector: 'app-data-studio-redesign',
  templateUrl: './data-studio-redesign.component.html',
  // to do: remove signals-preview.component.scss styles after refactor this component
  styleUrls: ['./data-studio-redesign.component.scss', './components/signals-preview/signals-preview.component.scss'],
  standalone: false
})

export class DataStudioRedesignComponent implements OnInit, OnDestroy {
  constructor(
    private dataStudioService: DataStudioRedesignService,
    private dataStudioHelper: DataStudioHelperService,
    private translate: TranslateService,
    private currency: CurrencyService,
    private timezoneService: TimezoneService
  ) {
    this.turbineSelected$ = new Subject();
    this.devicesSelected$ = new Subject();
    this.signalsSelected$ = new Subject();
    this.signalSelected$ = new Subject();
    this.queryParam = new Subject();
    this.turbineSelectedObservable = this.turbineSelected$.asObservable();
    this.turbinesSelectedObservable = this.devicesSelected$.asObservable();
    this.unsubscribe$ = new Subject();
  }
  @ViewChild('multiSignalsSelect', { static: false }) multiSignalsSelect: MatSelect;
  @ViewChild('multiTurbinesSelect', { static: false }) multiTurbinesSelect: MatSelect;

  allSelected = false;
  multiSignalAllSelected = false;
  isPreviewActivated = false;
  view;
  isSignalsDisabled = true;
  isTurbineDisabled = true;
  signalsSelected$: Subject<Array<string>>;
  signals$: any[] = [];
  signalsAvailables: any;
  isMultipleAssetAvailable = true;
  singleAssetLabel = 'TURBINE';

  signalSelected$: Subject<string>;
  turbines: any;
  turbinesNames: string[];
  signalsNames: string[];
  selectedDevice: string;
  selectedSignal: string;
  unsubscribe$: Subject<void>;
  devicesSelected$: Subject<{ _id: string, name?: string, type?: string, code?: number, serialNav?: string }[]>;
  views: DataStudioPageNavigationItem[];
  selectedDevicesNames: string[] = [];
  selectedDevices: any;

  singleTurbine = new UntypedFormControl();
  multiSignals = new UntypedFormControl();
  singleSignal = new UntypedFormControl();
  multiDevices = new UntypedFormControl();

  multiSignalsText: string;
  multiTurbinesText: string;
  allSelectedTurbines = false;

  csSignalIDs: Subject<string[]> = new Subject();
  metmastSignalsIds: Subject<string[]> = new Subject();
  controlSystemIDs: Subject<string[]> = new Subject();
  metmastsSensorIds: Subject<string[]> = new Subject();
  unitsSelected: string[] = [];
  allUnitsCount: number = 0;
  MAX_UNITS_COUNT: number = 2;
  series: PreviewSerieType[];
  seriesMultiDevice: PreviewSerieType[];
  selectedTab: number = 0;
  unitsMultiDeviceSelected: string[] = [];
  refreshGraph = new Subject();
  loadingData: boolean = true;

  private dateData: any;
  private queryParam: Subject<QueryParams>;
  private turbinesSelectedObservable: Observable<{ _id: string, name?: string, type?: string, code?: number, serialNav?: string }[]>;
  private turbineSelected$: Subject<({
    __typename?: 'Turbine';
  } & Pick<Turbine, '_id' | 'name' | 'type' | 'code' | 'serialNav'>)>;
  private turbineSelectedObservable: Observable<({
    __typename?: 'Turbine';
  } & Pick<Turbine, '_id' | 'name' | 'type' | 'code' | 'serialNav'>)>;

  get isParkDataView() {
    return this.view.id === 4;
  }

  ngOnInit() {
    this.setViews();

    this.turbineSelected$
      .pipe(filter(turbine => !!turbine), takeUntil(this.unsubscribe$))
      .subscribe(turbines => {
        this.selectedDevices = this.isParkDataView ? this.turbines : this.turbines?.filter(t => t.selected);
        this.selectedDevicesNames = this.selectedDevices?.map(t => this.getAssetName(t));
      }
      );

    this.getTurbines();

    this.dataStudioService.getSignals().pipe(
      tap(() => {
        this.signals$ = [];
        this.multiSignals.patchValue([]);
      }),
      filter(e => !!e),
      takeUntil(this.unsubscribe$)
    ).subscribe(e => {
      this.signals$ = e;
    });

    this.dataStudioHelper.getDateObservable$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(dateData => {
        const property = this.dataStudioHelper.typeSelection === DEVICE_TYPES.TURBINE ? 'turbines' : 'devices';
        if (dateData.fileRequest?.signals.length > 0 && (dateData.fileRequest[property].length > 0 || dateData.fileRequest.park)) {
          this.dateData = dateData;
          if (this.dataStudioHelper.typeSelection === DEVICE_TYPES.TURBINE) {
            const signalsSelected = [
              ...this.signals$.filter(e => dateData.fileRequest.signals.includes(e.value)).map(e => e.value)
            ];
            this.signalsAvailables = [...this.signals$.filter(e => dateData.fileRequest.signals.includes(e.value))];
            this.allUnitsCount = [...new Set(this.signalsAvailables.map(signal => signal.unit))].length;
            this.signalsNames = this.signalsAvailables.map(signal => this.translate.instant(signal.name));
            this.selectedSignal = this.signalsNames[0];
            const assetSelected = this.turbines.find(e => this.isParkDataView ? e.name.startsWith(dateData.fileRequest.park) : dateData.fileRequest.turbines.includes(e._id));
            this.singleTurbine.patchValue(assetSelected);
            this.multiSignals.patchValue([signalsSelected[0]]);
            this.triggerSignalsChange([signalsSelected[0]]);
            this.triggerTurbineChange({ index: this.turbinesNames.indexOf(this.getAssetName(assetSelected)) });

            if (!this.isParkDataView) {
              this.multiDevices.patchValue([assetSelected]);
              this.singleSignal.patchValue(this.signalsAvailables[0].value);
              this.triggerSignalChange({ index: 0 });
              this.triggerTurbinesChange([assetSelected]);
            }
          }
          this.setQueryParams();
        }
      }
      );

    this.timezoneService.getTimezoneObservable()
      .pipe(takeUntil(this.unsubscribe$), skip(1))
      .subscribe(() => {
        if (this.dateData) {
          this.setQueryParams();
        }
      }
      )

    this.setSingleTurbine();
    this.setMultipleTurbines();

    this.subscribeLanguageChange();
  }

  selLineChartData(signalsData) {
    const hasMinMaxDataFromServer = signalsData?.length === 2 && signalsData[1][0]?.min;
    const data = hasMinMaxDataFromServer ? signalsData[0] : signalsData;
    const timestamp = data.map(item => item.Timestamp);
    this.series = setSeries(
      data.map(item => ({ ...item, Timestamp: undefined, Serial: undefined })) as DSSignalItemData[],
      this.currency,
      this.translate,
      'signal'
    ).map(item => {
      return {
        type: 'line',
        name: item.label,
        unit: item.unit,
        yAxis: this.unitsSelected.indexOf(item.unit),
        legendSymbol: 'rectangle',
        data: item.data.map((point, index) => [timestamp[index], point])
      };
    });
    this.singleAssetLabel = this.setAssetLabel();
    this.loadingData = false;
  }

  triggerOnePerOneSignal(unit) {
    if (this.allSelected) {
      this.multiSignalsSelect.options.find(e => e.value === 0)?.deselect();
    }
    const signals = this.multiSignalsSelect.options.filter(e => e.selected).map(v => v.value);
    if (signals.length === this.signalsAvailables.length) {
      this.multiSignalsSelect.options.find(e => e.value === 0)?.select();
      this.allSelected = true;
    }
    this.triggerSignalsChange(signals, unit);
  }

  triggerAllSignals() {
    if (this.allUnitsCount > this.MAX_UNITS_COUNT) return;
    this.allSelected = !this.allSelected;
    if (!this.allSelected) {
      this.multiSignalsSelect.options.forEach(e => e.deselect());
    } else {
      this.multiSignalsSelect.options.forEach(e => e.select());
    }
    const signals = this.multiSignalsSelect.options.filter(e => e.selected && e.value !== 0).map(v => v.value);
    this.triggerSignalsChange(signals);
  }


  triggerOnePerOneTurbine() {
    if (this.allSelectedTurbines) {
      this.multiTurbinesSelect.options.find(e => e.value === 0).deselect();
    }
    const turbines = this.multiTurbinesSelect.options.filter(e => e.selected).map(v => v.value);
    if (turbines.length === this.turbines.length) {
      this.multiTurbinesSelect.options.find(e => e.value === 0).select();
      this.allSelectedTurbines = true;
    }
    this.triggerTurbinesChange(turbines);
  }

  triggerAllTurbines() {
    this.allSelectedTurbines = !this.allSelectedTurbines;
    if (!this.allSelectedTurbines) {
      this.multiTurbinesSelect.options.forEach(e => e.deselect());
    } else {
      this.multiTurbinesSelect.options.forEach(e => e.select());
    }
    this.triggerTurbinesChange(this.multiTurbinesSelect.options.filter(e => e.selected && e.value !== 0).map(v => v.value));
  }

  triggerSignalsChange(signals, unit = null) {
    const searchUnit = this.signalsAvailables.reduce((tot, { value, unit }, index) => {
      if (signals.includes(value)) {
        tot.push({ name: this.signalsNames[index], unit, value })
      }
      return tot;
    }, []);
    this.unitsSelected = [...new Set(searchUnit.map(({ unit }: { unit: string }) => unit))] as string[];
    this.multiSignalsText = searchUnit.map(({ name, unit }) => `${name} (${unit})`).join(', ');
    if (unit && (this.isSignalSelected(unit) && [...new Set(this.unitsSelected)].length <= this.MAX_UNITS_COUNT ||
      !this.isSignalSelected(unit) && [...new Set(this.unitsSelected)].length < this.MAX_UNITS_COUNT) || !unit) {
      this.loadingData = true;
      this.dataStudioHelper.typeSelection === DEVICE_TYPES.TURBINE ?
        this.signalsSelected$.next(searchUnit.map(({ value }) => value)) :
        this.csSignalIDs.next(searchUnit.map(({ value }) => value));
    }
  }

  triggerSignalChange(event) {
    this.loadingData = true;
    this.selectedSignal = event.option || this.signalsNames[event.index];
    this.signalSelected$.next(this.signalsAvailables[event.index].value);
  }

  triggerTurbineChange({ index }) {
    this.loadingData = true;
    const isControlSystem = this.dataStudioHelper.typeSelection === DEVICE_TYPES.CONTROL_SYSTEM;
    const isMetmast = this.dataStudioHelper.typeSelection === DEVICE_TYPES.METMAST;
    const notTurbine = isControlSystem || isMetmast;
    if (this.isPreviewActivated) {
      const selectedDevices = notTurbine ? this.selectedDevices.filter(device => device.selected) : this.turbines.filter(t => this.isParkDataView ? true : t.selected);
      this.selectedDevice = this.getAssetName(selectedDevices[index]);
      notTurbine ? this.devicesSelected$.next([selectedDevices[index]]) : this.turbineSelected$.next(selectedDevices[index]);
    } else {
      if (notTurbine) {
        this.selectedDevice = this.selectedDevicesNames[index];
        this.devicesSelected$.next([this.selectedDevices[index]]);
      } else {
        this.selectedDevice = this.turbinesNames[index];
        this.turbineSelected$.next(this.turbines[index]);
      }
    }
  }

  triggerTurbinesChange(e) {
    this.loadingData = true;
    this.multiTurbinesText = e.map(t => this.getAssetName(t)).join(', ');
    this.devicesSelected$.next(e);
  }

  closePreview() {
    this.isPreviewActivated = false;
  }

  getAssetName(asset) {
    return asset?.fe_name || asset?.code?.replaceAll('_', '');
  }

  isSignalSelected(unit: string) {
    return this.unitsSelected.includes(unit);
  }

  setView(event) {
    this.view = event;
    this.switchPages(event.id);
    this.dataStudioHelper.typeSelection = DEVICE_TYPES.TURBINE;
  }

  switchPages(i) {
    this.signalsSelected$.next([]);
    this.turbineSelected$.next(null);
    this.queryParam.next(null);
    this.devicesSelected$.next([]);
    this.signalSelected$.next(null);
    switch (i) {
      case 1:
        this.isMultipleAssetAvailable = true;
        this.singleAssetLabel = 'TURBINE';
        break;
      case 2:
        this.isMultipleAssetAvailable = true;
        this.singleAssetLabel = 'TURBINE';
        break;
      case 3:
        break;
      case 4:
        this.isMultipleAssetAvailable = false;
        this.singleAssetLabel = 'PARK';
        break;
    }
    this.isPreviewActivated = false;
  }

  private getTurbines() {
    this.dataStudioService.getTurbines().pipe(
      tap(() => {
        this.singleTurbine.patchValue([]);
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe(e => {
      this.turbines = e;
      this.turbinesNames = this.turbines.map(t => this.getAssetName(t));
      this.selectedDevices = this.isParkDataView ? this.turbines : this.turbines.filter(t => t.selected);
      this.selectedDevicesNames = this.selectedDevices.map(t => this.getAssetName(t));
      this.selectedDevice = this.turbinesNames[0];
    });
  }

  private setSingleTurbine() {
    this.dataStudioService.requestSingleTurbine(this.turbineSelectedObservable, this.signalsSelected$, this.queryParam, this.refreshGraph.pipe(startWith('default')))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((e: any) => {
        if (e) {
          this.dataStudioService.requestingSignalsData = false;
          this.selLineChartData(e);
        }
      }
      );
  }

  private setMultipleTurbines() {
    this.dataStudioService.requestMultipleTurbineParallel(this.turbinesSelectedObservable, this.signalSelected$, this.queryParam, this.refreshGraph.pipe(startWith('default')))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(response => {
        if (!response.length) {
          this.seriesMultiDevice = [];
        }
        this.singleAssetLabel = this.setAssetLabel();
        let resultArray = [];
        response.forEach(arr => {
          const hasMinMaxDataFromServer = arr?.length === 2 && arr[1][0]?.min;
          const data = hasMinMaxDataFromServer ? arr[0] : arr;
          resultArray = resultArray.concat(...data);
          const timestamp = resultArray.map(item => item.Timestamp);
          this.seriesMultiDevice = setSeries(
            resultArray.map(item => ({ ...item, Timestamp: undefined, Serial: undefined })) as DSSignalItemData[],
            this.currency,
            this.translate,
            'device'
          ).map(item => {
            return {
              type: 'line',
              name: item.label,
              unit: item.unit,
              legendSymbol: 'rectangle',
              data: item.data.map((point, index) => [timestamp[index], point])
            };
          });
          this.unitsMultiDeviceSelected = [this.series?.[0]?.unit];
        })

        this.loadingData = false;
        this.dataStudioService.requestingSignalsData = false;
      }
      );
  }

  private setAssetLabel(): string {
    return this.isParkDataView ? 'PARK' :
      (this.dataStudioHelper.typeSelection === DEVICE_TYPES.CONTROL_SYSTEM || this.dataStudioHelper.typeSelection === DEVICE_TYPES.METMAST) ?
        'DEVICE' : 'TURBINE'
  }

  private setViews() {
    /* This query disables the 'Park Data' tab if all the parks on the portfolio have the energyMeterAvailable to false */
    this.dataStudioService.getAllParkAndTurbines().pipe(take(1)).subscribe((data: any) => {
      this.views = [
        { id: 0, name: 'DATA_STUDIO.PAGER.DATA_ANALYSIS', disabledForPortfolio: false, active: true },
        { id: 1, name: 'DATA_STUDIO.PAGER.SCADA', disabledForPortfolio: false, active: false },
        { id: 2, name: 'DATA_STUDIO.PAGER.REAL_TIME_DATA', disabledForPortfolio: data.every(p => !p.data30sAvailable), active: false },
        { id: 3, name: 'DATA_STUDIO.PAGER.TURBINE_LOGS', disabledForPortfolio: false, active: false },
        { id: 4, name: 'DATA_STUDIO.PAGER.PARK_DATA', disabledForPortfolio: data.every(p => !p.energyMeterAvailable), active: false },
        { id: 5, name: 'DATA_STUDIO.PAGER.METADATA', disabledForPortfolio: false, active: false },
      ];
      this.views = this.views.filter(v => !v.disabledForPortfolio);
      this.view = this.views[0];
    });
  }

  private subscribeLanguageChange() {
    moment.locale(this.translate.currentLang);
    this.translate.onLangChange.pipe(
      skip(1)
    ).subscribe((event: LangChangeEvent) => {
      moment.locale(event.lang);
      this.refreshGraph.next(event.lang);
    });
  }

  private setQueryParams() {
    this.queryParam.next({
      queryType: this.dateData.fileRequest.queryType,
      granularity: this.dateData.fileRequest.granularity,
      startTS: this.dateData.fileRequest.startTS,
      endTS: this.dateData.fileRequest.endTS
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
